import './App.scss';
import { ApolloClient, createHttpLink, InMemoryCache, ApolloProvider } from '@apollo/client';
import { CompensationVouchersHistoryObj } from '../../models/home';
import { HistoryProvider } from '../../providers/HistoryProvider';
import { LANGUAGE } from '../../constants/common';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { setContext } from '@apollo/client/link/context';
import { useApiError } from '../../providers/ApiErrorProvider';
import { useAuthProvider } from '../../providers/AuthProvider';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AppError from '../../components/AppCommon/AppError/AppError';
import AppNavBar from '../../components/AppCommon/AppNavBar/AppNavBar';
import History from '../History/History';
import Home from '../Home/Home';
import i18next from 'i18next';
import Login from '../Login/Login';
import ProtectedRoute from '../../components/AppCommon/ProtectedRoute/ProtectedRoute';
import { NotificationsProvider } from '../../providers/NotificationProvider';
import { OfflineRequestsProvider } from '../../providers/OfflineRequestsProvider';
import { MasterDataProvider } from '../../providers/MasterDataProvider';

const App = () => {
  const authContext = useAuthProvider();
  const apiErrorContext = useApiError();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const [activeMenuIndex, setActiveMenuIndex] = useState(-1);

  const authLink = setContext((_, { headers }) => {
    const token = authContext?.user?.accessToken;
    return {
      headers: {
        ...headers,
        authorization: token ? `${token}` : '',
      }
    }
  });
  
  const httpLink = createHttpLink({
    uri: process.env.REACT_APP_GRAPHQL_API,
  });
  
  const client = new ApolloClient({
    link: authLink.concat(httpLink),
    cache: new InMemoryCache()
  });
  
  useEffect(() => {
    const compensationVouchersHistoryObj: CompensationVouchersHistoryObj = {
      compensationVouchersHistory: []
    };

    if (!localStorage.getItem('compensationVouchersHistoryObj')) {
      localStorage.setItem('compensationVouchersHistoryObj', JSON.stringify(compensationVouchersHistoryObj));
    }

    const voucherIdsUsedArray: any[] = [];

    if (!localStorage.getItem('voucherIdsUsedArray')) {
      localStorage.setItem('voucherIdsUsedArray', JSON.stringify(voucherIdsUsedArray));
    }

  }, []);

  useEffect(() => {
    const path = location.pathname.split('/')[1];
    if (location.hash || path === 'login') return;
    if (path === LANGUAGE.en || path === LANGUAGE.fr) {
      i18next.changeLanguage(path);
    } else {
      navigate('/en');
    }
  }, [location, i18n, navigate]);

  useEffect(() => {
    document.title = i18n.t('appName');
  }, [i18n.language, i18n]);


  return (
    <main className="App">
      {apiErrorContext.error? <AppError /> : null}
      <ApolloProvider client={client}>
        {activeMenuIndex > -1 && (
          <AppNavBar
            activeMenuIndex={activeMenuIndex}
            navigate={navigate}
            setActiveMenuIndex={setActiveMenuIndex}
          />
        )}
        <div className="c-full-page">
          <div className="back-drop-filter" id="back-drop-filter" />
            <OfflineRequestsProvider>
              <NotificationsProvider>
                <Routes>
                  <Route path="/login" element={<Login setActiveMenuIndex={setActiveMenuIndex} />} />
                    <Route path="/:lang" element={
                      <ProtectedRoute>
                        <MasterDataProvider>
                          <Home setActiveMenuIndex={setActiveMenuIndex}/>
                        </MasterDataProvider>
                      </ProtectedRoute>}
                    />
                    <Route path="/:lang/history" element={
                      <ProtectedRoute>
                        <MasterDataProvider>
                          <HistoryProvider>
                            <History navigate={navigate} setActiveMenuIndex={setActiveMenuIndex} />
                          </HistoryProvider>
                        </MasterDataProvider>
                      </ProtectedRoute>}
                    />
                </Routes>
              </NotificationsProvider>
            </OfflineRequestsProvider>
        </div>
      </ApolloProvider>
    </main>
  );
};

export default App;
